import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import type { TriggerProps as PopupTriggerProps } from '@atlaskit/popup/types';
import { Box } from '@atlaskit/primitives/compiled/box';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { BylineConnectionsTrigger, BylineConnectionsWrapper } from './BylineConnectionsTrigger.tsx';
import { MAX_BYLINE_CONNECTION_ICONS } from './utils/getBylineConnectionIcons.ts';
import { createPlaceholderConnectionIcon } from './utils/placeholderConnectionIcon.tsx';

const i18n = defineMessages({
	loadingConnections: {
		id: 'byline-connections.loading',
		defaultMessage: 'Loading connections',
		description: 'Accessible label for the connections byline loading spinner',
	},
});

const styles = cssMap({
	loadingMenu: {
		display: 'flex',
		justifyContent: 'center',
		width: '320px',
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.medium'),
	},
});

type BylineConnectionsPlaceholderVisualProps = {
	testId?: string;
};

const placeholderIcons = Array.from({ length: MAX_BYLINE_CONNECTION_ICONS }, (_, index) =>
	createPlaceholderConnectionIcon(index),
);

/**
 * The Teamwork Graph logo + label + app-icon stack placeholder shown while the byline
 * connections item is resolving. Shared by the SSR/loadable placeholder and the
 * loaded component's own loading state so the footprint lines up across the
 * SSR → client-load → data-load → item handoff, avoiding layout shift. All loading
 * states reserve three tiles; the loaded stack fills spare slots with AI tool icons.
 *
 * Clicking it opens a popup with a spinner. The popup closes on Esc or an outside click, and
 * goes away with the placeholder once the resolved byline takes its place.
 */
export const BylineConnectionsPlaceholderVisual = ({
	testId = 'byline-connections-ssr-placeholder',
}: BylineConnectionsPlaceholderVisualProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { Popup } = useChoreographerAssets();
	const [isOpen, setIsOpen] = useState(false);

	return (
		<BylineConnectionsWrapper
			role="status"
			aria-label={formatMessage(i18n.loadingConnections)}
			data-testid={testId}
		>
			<Popup
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				placement="bottom-start"
				content={() => (
					<Box
						xcss={styles.loadingMenu}
						role="dialog"
						aria-busy="true"
						aria-label={formatMessage(i18n.loadingConnections)}
					>
						<Spinner size="medium" testId="byline-connections-loading-menu-spinner" />
					</Box>
				)}
				trigger={(triggerProps: PopupTriggerProps) => (
					<BylineConnectionsTrigger
						appearance="placeholder"
						links={[]}
						additionalIcons={placeholderIcons}
						buttonProps={{
							...triggerProps,
							onClick: () => setIsOpen((current) => !current),
							'aria-expanded': isOpen,
							'aria-haspopup': 'dialog',
						}}
					/>
				)}
			/>
		</BylineConnectionsWrapper>
	);
};
